import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
} from "recharts";
import {
  Eye, Wind, GlassWater, Search, FileDown, GitCompareArrows, Clock, Smartphone,
  Camera, PenLine, Save, Award, Star, ChevronRight, Menu, X, Check, Quote, GraduationCap,
} from "lucide-react";import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import AgeGate from "@/components/AgeGate";
import GuideDownloadCTA from "@/components/GuideDownloadCTA";

const HERO = "https://images.unsplash.com/photo-1606767208159-1a5fb0a87841?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const SWIRL = "https://images.unsplash.com/photo-1630369160812-26c7604cbd8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CELLAR = "https://images.unsplash.com/photo-1578911373434-0cb395d2cbfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const C = { bg: "#F7F4EF", borgona: "#5A1E2C", vino: "#8B2635", oro: "#C8A96A", texto: "#222222", gris: "#777777" };

const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const GLOSARIO_PEEK = [
  ["Taninos", "Compuestos que aportan estructura y esa sensación astringente típica de los tintos."],
  ["Retrogusto", "El sabor que permanece en boca tras tragar; cuanto más largo y agradable, mejor."],
  ["Lágrima", "Las gotas que resbalan por la copa al girarla; delatan alcohol y cuerpo."],
  ["Aromas terciarios", "Notas que aparecen con el envejecimiento: cuero, tabaco, cacao, frutos secos."],
];

const GlossaryPeek = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % GLOSARIO_PEEK.length), 3200);
    return () => clearInterval(id);
  }, []);
  const [term, def] = GLOSARIO_PEEK[i];
  return (
    <div data-testid="glosario-peek" className="rounded-2xl p-5 mb-8 min-h-[104px] flex items-center" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(200,169,106,0.25)" }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={term}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] mb-1.5" style={{ color: "#C8A96A" }}>Del glosario</p>
          <p className="text-white"><span className="font-serif text-xl">{term}.</span> <span className="text-white/70 text-[15px]">{def}</span></p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};


const BENEFICIOS = [
  { icon: Award, t: "Registro profesional", d: "Captura todos los aspectos visuales, olfativos y gustativos siguiendo una metodología completa." },
  { icon: Search, t: "Organiza tu colección", d: "Encuentra cualquier vino por país, región, bodega, variedad, añada o puntuación." },
  { icon: FileDown, t: "Exporta a PDF", d: "Genera una ficha profesional lista para compartir, imprimir o archivar." },
  { icon: GitCompareArrows, t: "Compara vinos", d: "Selecciona hasta tres notas y descubre sus diferencias mediante gráficos radar." },
  { icon: Clock, t: "Evolución del vino", d: "Consulta cómo cambia un mismo vino con el paso del tiempo comparando catas." },
  { icon: Smartphone, t: "Siempre disponible", d: "Accede desde cualquier dispositivo sin instalar aplicaciones." },
];

const PASO_IMGS = {
  p1: "https://images.unsplash.com/photo-1671418285899-58fb1471c038?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  p2: "https://images.unsplash.com/photo-1642140848512-064778664e90?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  p3: "https://images.unsplash.com/photo-1776754373117-a4e06909cb25?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const PASOS = [
  { icon: Camera, n: "01", t: "Fotografía la botella", d: "Sube una imagen de la botella y su etiqueta para identificar el vino.", img: PASO_IMGS.p1 },
  { icon: PenLine, n: "02", t: "Completa la nota de cata", d: "Recorre las fases visual, olfativa y gustativa con metodología profesional.", img: PASO_IMGS.p2 },
  { icon: Save, n: "03", t: "Guarda, exporta y compara", d: "Archiva tu ficha, expórtala a PDF y compárala con otras catas.", img: PASO_IMGS.p3 },
];

const FUNCS = [
  { icon: Eye, t: "Examen Visual", items: ["Color", "Intensidad", "Limpidez", "Viscosidad", "Efervescencia"] },
  { icon: Wind, t: "Examen Olfativo", items: ["Primera nariz", "Segunda nariz", "Intensidad", "Complejidad", "Familias aromáticas", "Persistencia"] },
  { icon: GlassWater, t: "Examen Gustativo", items: ["Ataque", "Acidez", "Alcohol", "Taninos", "Astringencia", "Equilibrio", "Final", "Caudalías"] },
  { icon: Award, t: "Evaluación profesional", items: ["Puntuación", "Pronóstico de guarda", "Maridaje sugerido", "Temperatura de servicio", "Observaciones"] },
  { icon: GitCompareArrows, t: "Comparador inteligente", items: ["Hasta tres vinos", "Gráfico radar", "Acidez · Taninos · Alcohol", "Persistencia · Complejidad", "Intensidad · Equilibrio"] },
  { icon: FileDown, t: "Exportación", items: ["PDF profesional", "Diseño elegante", "Listo para compartir"] },
  { icon: Clock, t: "Historial", items: ["Degustaciones ordenadas", "Vista cronológica", "Acceso instantáneo"] },
  { icon: Search, t: "Búsqueda", items: ["País · Bodega · Añada", "Región · Variedad", "Puntuación · Fecha"] },
];

const RADAR_DATA = [
  { attr: "Acidez", A: 4.5, B: 3.2, C: 2.8 },
  { attr: "Taninos", A: 4.0, B: 4.6, C: 2.0 },
  { attr: "Alcohol", A: 3.8, B: 4.2, C: 3.0 },
  { attr: "Complejidad", A: 4.7, B: 3.5, C: 3.2 },
  { attr: "Persistencia", A: 4.3, B: 3.8, C: 2.5 },
  { attr: "Intensidad", A: 4.6, B: 4.0, C: 3.4 },
  { attr: "Equilibrio", A: 4.8, B: 3.6, C: 3.0 },
];

const TESTIMONIOS = [
  { t: "Como sommelier cato decenas de vinos por semana. Vinonotas me permite recuperar cualquier ficha en segundos y comparar añadas con precisión.", n: "Lucía Fernández", r: "Sommelier" },
  { t: "La metodología estructurada es idéntica a la que uso en bodega. Exportar el PDF para el equipo técnico nos ahorra horas de trabajo.", n: "Martín Alcaraz", r: "Enólogo" },
  { t: "Tengo más de 400 botellas. Por fin puedo recordar por qué me enamoró cada una y decidir cuándo abrirlas.", n: "Diego Rojas", r: "Coleccionista" },
];

function DemoNote({ open, onOpenChange }) {
  const rows = [
    ["Vista", "Rojo rubí intenso · Capa alta · Lágrima densa"],
    ["Nariz", "Fruta negra madura, regaliz, vainilla y tabaco. Intensidad alta."],
    ["Boca", "Entrada amplia, taninos pulidos, acidez equilibrada, final largo (8 caudalías)."],
    ["Guarda", "En su punto · Potencial 5-8 años"],
    ["Maridaje", "Carnes rojas, cordero, quesos curados"],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl border-0" data-testid="demo-note-dialog">
        <DialogTitle className="sr-only">Ficha de cata de ejemplo — Ribera Reserva 2018</DialogTitle>
        <div className="p-6" style={{ background: C.borgona }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: C.oro }}>Ficha de cata · ejemplo</p>
              <h3 className="font-serif text-2xl text-white mt-1">Ribera Reserva 2018</h3>
              <p className="text-white/70 text-sm">Bodega Valdemar · Ribera del Duero · Tempranillo</p>
            </div>
            <div className="text-center">
              <div className="font-mono text-4xl font-bold" style={{ color: C.oro }}>93</div>
              <div className="text-[10px] uppercase tracking-wider text-white/70">Excelente</div>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white divide-y divide-black/5">
          {rows.map(([k, v]) => (
            <div key={k} className="py-3">
              <p className="text-xs uppercase tracking-wider mb-0.5" style={{ color: C.vino }}>{k}</p>
              <p className="text-sm" style={{ color: C.texto }}>{v}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FLOW = [
  { key: "visual", icon: Eye, label: "Visual", chips: ["Limpio", "Amarillo pajizo", "Brillante", "Capa media"] },
  { key: "olfativo", icon: Wind, label: "Olfativo", chips: ["Cítricos", "Flor blanca", "Mineral", "Intensidad alta"] },
  { key: "gustativo", icon: GlassWater, label: "Gustativo", chips: ["Acidez alta", "Cuerpo medio", "Final largo", "8 caudalías"] },
  { key: "conclusion", icon: Award, label: "Conclusión", score: 93 },
];

function FlowDemo() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhase((p) => (p + 1) % FLOW.length), 2600);
    return () => clearInterval(t);
  }, []);
  const cur = FLOW[phase];
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-wider" style={{ color: C.oro }}>Nueva cata</p>
        <span className="text-[11px]" style={{ color: C.gris }}>Paso {phase + 1} de 4</span>
      </div>
      {/* mini stepper */}
      <div className="flex items-center gap-1.5 mb-5">
        {FLOW.map((f, i) => {
          const Icon = f.icon;
          const active = i === phase;
          const done = i < phase;
          return (
            <React.Fragment key={f.key}>
              <span className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 shrink-0"
                style={{ background: active ? C.borgona : done ? C.oro : "#ece4d8", color: active ? "#fff" : done ? C.borgona : C.gris }}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </span>
              {i < FLOW.length - 1 && <span className="h-px flex-1 rounded" style={{ background: i < phase ? C.oro : "#ece4d8" }} />}
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex-1 min-h-[150px]">
        <AnimatePresence mode="wait">
          <motion.div key={cur.key}
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.4 }}>
            <h4 className="font-serif text-lg mb-4" style={{ color: C.texto }}>Examen {cur.label}</h4>
            {cur.chips ? (
              <div className="flex flex-wrap gap-2">
                {cur.chips.map((c, ci) => (
                  <motion.span key={c}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + ci * 0.18 }}
                    className="text-sm px-3.5 py-1.5 rounded-full"
                    style={{ background: ci === 0 ? C.borgona : "#f0e9df", color: ci === 0 ? "#fff" : C.texto }}>
                    {c}
                  </motion.span>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-5 pt-2">
                <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }}
                  className="font-mono text-6xl font-bold" style={{ color: C.oro }}>{cur.score}</motion.div>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.borgona }}>Excelente</p>
                  <p className="text-sm" style={{ color: C.gris }}>Guarda 5-8 años · Carnes rojas</p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#beneficios", label: "Características" },
    { href: "#como", label: "Cómo funciona" },
    { href: "#comparador", label: "Comparador" },
    { href: "#precios", label: "Precios" },
  ];

  const primaryBtn = "inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl";

  return (
    <div style={{ background: C.bg, color: C.texto }} className="font-sans overflow-x-hidden">
      <AgeGate />
      <DemoNote open={demo} onOpenChange={setDemo} />

      {/* NAV */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#F7F4EF]/90 backdrop-blur-xl shadow-sm py-3" : "py-5"}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/vinote-logo.png" alt="Vinonotas" className="w-9 h-9 object-contain rounded-md" />
            <span className="font-serif text-xl" style={{ color: C.borgona }}>Vinonotas</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <a key={l.href} href={l.href} className="text-sm font-medium transition-colors hover:opacity-70" style={{ color: C.texto }}>{l.label}</a>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate("/login")} data-testid="nav-login-btn" className="text-sm font-semibold px-4 py-2 transition-colors hover:opacity-70" style={{ color: C.borgona }}>Iniciar sesión</button>
            <button onClick={() => navigate("/register")} data-testid="nav-register-btn" className={`${primaryBtn} text-sm px-5 py-2.5 text-white`} style={{ background: C.borgona }}>Empieza gratis</button>
          </div>
          <button className="md:hidden" onClick={() => setMenuOpen(true)} data-testid="landing-menu-btn"><Menu style={{ color: C.borgona }} /></button>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-72 bg-[#F7F4EF] p-6 shadow-2xl">
            <button className="absolute top-5 right-5" onClick={() => setMenuOpen(false)}><X style={{ color: C.gris }} /></button>
            <div className="mt-10 flex flex-col gap-5">
              {links.map((l) => <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)} className="text-lg font-medium" style={{ color: C.texto }}>{l.label}</a>)}
              <hr className="border-black/10" />
              <button onClick={() => navigate("/login")} className="text-left font-semibold" style={{ color: C.borgona }}>Iniciar sesión</button>
              <button onClick={() => navigate("/register")} className={`${primaryBtn} px-5 py-3 text-white`} style={{ background: C.borgona }}>Empieza gratis</button>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section id="top" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="Cata profesional de vino" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,8,12,0.72) 0%, rgba(20,8,12,0.55) 40%, rgba(20,8,12,0.88) 100%)" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center pt-24">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] mb-6" style={{ color: C.oro }}>
            La memoria profesional de cada vino que catas
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
            Cada vino cuenta una historia.<br />
            <span style={{ color: C.oro }}>Regístrala como un profesional.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="text-white/85 text-base sm:text-lg max-w-2xl mx-auto mt-7 leading-relaxed">
            Vinonotas transforma cada degustación en un registro técnico, elegante y consultable para que nunca vuelvas a olvidar un gran vino.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <button onClick={() => navigate("/register")} data-testid="hero-register-btn" className={`${primaryBtn} text-base px-8 py-4 text-white w-full sm:w-auto`} style={{ background: C.borgona }}>
              Empieza gratis <span className="opacity-70 font-normal">(hasta 10 catas)</span>
            </button>
            <button onClick={() => navigate("/login")} data-testid="hero-login-btn" className={`${primaryBtn} text-base px-8 py-4 w-full sm:w-auto border border-white/50 text-white hover:bg-white/10`}>
              Inicio de sesión
            </button>
            <button onClick={() => setDemo(true)} data-testid="hero-demo-btn" className={`${primaryBtn} text-base px-8 py-4 w-full sm:w-auto border border-white/40 text-white hover:bg-white/10`}>
              Ver una nota de cata
            </button>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-white/50 text-sm mt-5">Sin tarjeta de crédito.</motion.p>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-5xl leading-tight" style={{ color: C.borgona }}>
              Todos recordamos un gran vino…<br />
              <span style={{ color: C.gris }}>hasta que dejamos de hacerlo.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-4 text-lg leading-relaxed" style={{ color: C.texto }}>
              <p>¿Cuántas veces has probado un vino extraordinario y meses después ya no recuerdas exactamente cómo era?</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center text-base pt-4" style={{ color: C.gris }}>
                <span>Las notas en papel se pierden.</span>
                <span className="hidden sm:inline">·</span>
                <span>Las fotografías no capturan aromas.</span>
                <span className="hidden sm:inline">·</span>
                <span>Las apps genéricas no siguen una metodología.</span>
              </div>
              <p className="pt-4 font-medium" style={{ color: C.borgona }}>Vinonotas fue creada para resolver ese problema.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section id="beneficios" className="py-20 sm:py-28" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>Beneficios</p>
            <h2 className="font-serif text-3xl sm:text-5xl" style={{ color: C.borgona }}>Diseñada para catadores exigentes</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFICIOS.map((b, i) => (
              <Reveal key={b.t} delay={i * 0.06}>
                <div className="group h-full p-7 rounded-2xl border transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(90,30,44,0.1)]" style={{ background: C.bg, borderColor: "rgba(0,0,0,0.06)" }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-colors" style={{ background: C.borgona }}>
                    <b.icon className="w-6 h-6" style={{ color: C.oro }} strokeWidth={1.75} />
                  </div>
                  <h3 className="font-serif text-xl mb-2" style={{ color: C.texto }}>{b.t}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: C.gris }}>{b.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="como" className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>En tres pasos</p>
            <h2 className="font-serif text-3xl sm:text-5xl" style={{ color: C.borgona }}>Cómo funciona</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {PASOS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.12}>
                <div className="group rounded-2xl overflow-hidden bg-white shadow-[0_10px_40px_rgba(90,30,44,0.08)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(90,30,44,0.16)]">
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.img} alt={p.t} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,10,14,0) 40%, rgba(26,10,14,0.65) 100%)" }} />
                    <span className="absolute top-4 left-4 w-11 h-11 rounded-xl flex items-center justify-center backdrop-blur-md" style={{ background: "rgba(247,244,239,0.9)" }}>
                      <p.icon className="w-5 h-5" style={{ color: C.borgona }} strokeWidth={1.75} />
                    </span>
                    <span className="absolute bottom-3 right-4 font-serif text-5xl" style={{ color: C.oro }}>{p.n}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl mb-2" style={{ color: C.texto }}>{p.t}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: C.gris }}>{p.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FUNCIONALIDADES */}
      <section className="py-20 sm:py-28" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>Funcionalidades</p>
            <h2 className="font-serif text-3xl sm:text-5xl" style={{ color: C.borgona }}>Todo lo que un amante del vino necesita</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FUNCS.map((f, i) => (
              <Reveal key={f.t} delay={(i % 4) * 0.06}>
                <div className="h-full p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg" style={{ borderColor: "rgba(0,0,0,0.07)" }}>
                  <f.icon className="w-7 h-7 mb-4" style={{ color: C.vino }} strokeWidth={1.6} />
                  <h3 className="font-serif text-lg mb-3" style={{ color: C.texto }}>{f.t}</h3>
                  <ul className="space-y-1.5">
                    {f.items.map((it) => (
                      <li key={it} className="flex items-center gap-2 text-sm" style={{ color: C.gris }}>
                        <Check className="w-3.5 h-3.5 shrink-0" style={{ color: C.oro }} /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA APRENDE A CATAR */}
      <section className="py-20 sm:py-28" style={{ background: C.bg }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl px-7 sm:px-14 py-12 sm:py-16" style={{ background: C.borgona }}>
              <div className="absolute -right-10 -top-10 opacity-[0.07]">
                <GraduationCap className="w-64 h-64" style={{ color: C.oro }} strokeWidth={1} />
              </div>
              <div className="relative">
                <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>Guía gratuita</p>
                <h2 className="font-serif text-3xl sm:text-5xl text-white mb-4 leading-tight">¿Nuevo en el mundo del vino?<br className="hidden sm:block" /> Empieza aquí</h2>
                <p className="text-base sm:text-lg text-white/70 max-w-2xl mb-8 leading-relaxed">
                  Aprende a catar como un profesional en minutos: las tres fases de la cata (visual, olfativa y gustativa)
                  explicadas con lenguaje sencillo, consejos prácticos y un glosario con todos los términos.
                </p>
                <div className="flex flex-wrap gap-3 mb-9">
                  {[["Visual", Eye], ["Olfativa", Wind], ["Gustativa", GlassWater]].map(([label, Icon]) => (
                    <span key={label} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(200,169,106,0.3)" }}>
                      <Icon className="w-4 h-4" style={{ color: C.oro }} /> {label}
                    </span>
                  ))}
                </div>
                <GlossaryPeek />
                <button
                  data-testid="landing-aprende-btn"
                  onClick={() => navigate("/aprende")}
                  className={`${primaryBtn} px-8 py-4 text-base inline-flex items-center gap-2`}
                  style={{ background: C.oro, color: C.borgona }}
                >
                  <GraduationCap className="w-5 h-5" /> Aprende a catar gratis
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LEAD MAGNET — DESCARGA DE GUÍA */}
      <section className="pb-20 sm:pb-28 -mt-6 sm:-mt-10" style={{ background: C.bg }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <Reveal>
            <GuideDownloadCTA theme="dark" />
          </Reveal>
        </div>
      </section>

      {/* MOCKUP */}
      <section className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>En acción</p>
            <h2 className="font-serif text-3xl sm:text-5xl" style={{ color: C.borgona }}>Tu bodega, elegante y organizada</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl overflow-hidden shadow-[0_40px_100px_rgba(90,30,44,0.25)] border border-black/5" style={{ background: "#fff" }}>
              <div className="flex items-center gap-2 px-5 py-3 border-b border-black/5" style={{ background: C.bg }}>
                <span className="w-3 h-3 rounded-full bg-[#E5A5A5]" /><span className="w-3 h-3 rounded-full bg-[#E8C98A]" /><span className="w-3 h-3 rounded-full bg-[#A8C79A]" />
                <span className="ml-3 text-xs" style={{ color: C.gris }}>vinonotas.com/app</span>
              </div>
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-7 border-r border-black/5">
                  <FlowDemo />
                </div>
                <div className="p-7 flex flex-col gap-5">
                  <div>
                    <p className="text-xs uppercase tracking-wider mb-3" style={{ color: C.oro }}>Perfil sensorial</p>
                    <ResponsiveContainer width="100%" height={170}>
                      <RadarChart data={RADAR_DATA} outerRadius={62}>
                        <PolarGrid stroke="#eadfd3" />
                        <PolarAngleAxis dataKey="attr" tick={{ fontSize: 9, fill: C.gris }} />
                        <Radar dataKey="A" stroke={C.borgona} fill={C.borgona} fillOpacity={0.25} strokeWidth={2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[["Catas", "128"], ["Vinos", "94"], ["Mejor", "97"], ["Prom.", "89"]].map(([k, v]) => (
                      <div key={k} className="rounded-xl p-2.5 text-center" style={{ background: C.bg }}>
                        <div className="font-mono text-lg font-bold" style={{ color: C.borgona }}>{v}</div>
                        <div className="text-[10px] uppercase tracking-wide" style={{ color: C.gris }}>{k}</div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setDemo(true)} className="inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: C.vino }}>
                    <FileDown className="w-4 h-4" /> Exportar PDF
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPARACIÓN (dark) */}
      <section id="comparador" className="py-24 sm:py-32" style={{ background: "#1A0A0E" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: C.oro }}>Comparador inteligente</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-white leading-tight">Descubre diferencias que antes pasaban desapercibidas.</h2>
            <p className="text-white/70 mt-6 leading-relaxed">Con una sola vista identifica diferencias en:</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {["Acidez", "Taninos", "Alcohol", "Complejidad", "Persistencia", "Intensidad", "Equilibrio"].map((a) => (
                <span key={a} className="text-sm px-3.5 py-1.5 rounded-full border border-white/15 text-white/80">{a}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <ResponsiveContainer width="100%" height={360}>
                <RadarChart data={RADAR_DATA}>
                  <PolarGrid stroke="rgba(255,255,255,0.15)" />
                  <PolarAngleAxis dataKey="attr" tick={{ fontSize: 12, fill: "rgba(255,255,255,0.7)" }} />
                  <PolarRadiusAxis domain={[0, 5]} tick={false} axisLine={false} />
                  <Radar name="Ribera Reserva 2018" dataKey="A" stroke={C.oro} fill={C.oro} fillOpacity={0.2} strokeWidth={2} />
                  <Radar name="Rioja Gran 2015" dataKey="B" stroke={C.vino} fill={C.vino} fillOpacity={0.2} strokeWidth={2} />
                  <Radar name="Albariño 2022" dataKey="C" stroke="#8FA98C" fill="#8FA98C" fillOpacity={0.15} strokeWidth={2} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#fff" }} />
                  <Tooltip contentStyle={{ background: "#2A1015", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EXPORTACIÓN PDF */}
      <section className="py-24 sm:py-32" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl blur-2xl opacity-20" style={{ background: C.borgona }} />
              <div className="relative rounded-2xl shadow-2xl border border-black/5 overflow-hidden" style={{ background: "#fff" }}>
                <div className="p-5" style={{ background: C.borgona }}>
                  <p className="font-serif text-xl text-white">Vinonotas</p>
                  <p className="text-[10px] tracking-[0.2em]" style={{ color: C.oro }}>FICHA DE CATA PROFESIONAL</p>
                </div>
                <div className="p-6 space-y-3">
                  {["Ribera Reserva 2018", "Bodega Valdemar · Tempranillo", "Vista · Nariz · Boca · Conclusión", "Puntuación: 93 / 100"].map((t, i) => (
                    <div key={t} className={`h-3 rounded-full ${i === 3 ? "w-1/2" : "w-full"}`} style={{ background: i === 3 ? C.oro : "#eee" }} />
                  ))}
                  <div className="pt-4 text-sm" style={{ color: C.gris }}>Ribera Reserva 2018 — 93 pts</div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: C.oro }}>Exportación</p>
            <h2 className="font-serif text-3xl sm:text-5xl" style={{ color: C.borgona }}>Informes profesionales en un clic</h2>
            <p className="text-lg mt-6 leading-relaxed" style={{ color: C.texto }}>
              Genera informes profesionales listos para compartir con clientes, colegas o conservar en tu biblioteca personal.
            </p>
            <button onClick={() => setDemo(true)} data-testid="pdf-example-btn" className={`${primaryBtn} mt-8 px-7 py-3.5 text-white`} style={{ background: C.borgona }}>
              Ver ejemplo PDF <ChevronRight className="w-4 h-4" />
            </button>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] mb-3" style={{ color: C.oro }}>Testimonios</p>
            <h2 className="font-serif text-3xl sm:text-5xl" style={{ color: C.borgona }}>Profesionales que recuerdan mejor</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIOS.map((t, i) => (
              <Reveal key={t.n} delay={i * 0.1}>
                <div className="h-full p-8 rounded-2xl border flex flex-col" style={{ background: "#fff", borderColor: "rgba(0,0,0,0.06)" }}>
                  <Quote className="w-8 h-8 mb-4" style={{ color: C.oro }} />
                  <p className="text-base leading-relaxed flex-1" style={{ color: C.texto }}>{t.t}</p>
                  <div className="flex items-center gap-1 mt-5" style={{ color: C.oro }}>
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                  </div>
                  <div className="mt-4 pt-4 border-t border-black/5">
                    <p className="font-semibold" style={{ color: C.texto }}>{t.n}</p>
                    <p className="text-sm" style={{ color: C.gris }}>{t.r}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="precios" className="py-24 sm:py-32" style={{ background: C.borgona }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-white leading-tight">Empieza hoy a construir tu biblioteca personal de vinos.</h2>
            <p className="text-white/75 text-lg mt-6">Registra gratuitamente tus primeras diez catas.</p>
            <button onClick={() => navigate("/register")} data-testid="cta-register-btn" className={`${primaryBtn} mt-10 px-10 py-5 text-lg`} style={{ background: C.oro, color: C.borgona }}>
              Empieza gratis <span className="opacity-70 font-normal">(hasta 10 catas)</span>
            </button>
            <p className="text-white/50 text-sm mt-5">Sin tarjeta de crédito.</p>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: "#140508" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row justify-between gap-8">
            <div className="flex items-center gap-2.5">
              <img src="/vinote-logo.png" alt="Vinonotas" className="w-9 h-9 object-contain rounded-md" />
              <span className="font-serif text-xl text-white">Vinonotas</span>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              {[["Inicio", "#top"], ["Características", "#beneficios"], ["Aprende a catar", "/aprende"], ["Precios", "#precios"], ["Contacto", "#precios"], ["Privacidad", "/privacidad"], ["Términos", "/terminos"]].map(([l, h]) => (
                <a key={l} href={h} className="text-sm text-white/60 hover:text-white transition-colors">{l}</a>
              ))}
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 text-center text-sm text-white/40">© {new Date().getFullYear()} Vinonotas. La memoria profesional de cada vino que catas.</div>
        </div>
      </footer>
    </div>
  );
}
